<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t('wms.packing_orders_title') }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="loadData()" :loading="loading">
            <i class="fa fa-refresh"></i> {{ $t('refresh') }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div v-if="summary.product_name" class="row align-items-center">
            <div class="col">
              <h3 class="mb-0">{{ summary.product_name }}</h3>
              <p class="text-muted mb-0" v-if="summary.label">
                {{ summary.type === 'platform' ? $t('wms.platform') : $t('wms.sale_channel') }}:
                <strong>{{ summary.label }}</strong>
              </p>
            </div>
            <div class="col-auto text-right">
              <span class="text-muted mr-3">
                {{ $t('wms.packing_total_orders') }}: <strong>{{ summary.total_orders }}</strong>
              </span>
              <span class="text-muted">
                {{ $t('wms.packing_total_quantity') }}: <strong>{{ summary.total_quantity }}</strong>
              </span>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table
            :data="orders"
            header-row-class-name="thead-light"
            stripe
          >
            <el-table-column
              :label="$t('wms.packing_orders_order_id')"
              prop="order_id"
              min-width="110"
            >
              <template slot-scope="{row}">{{ row.order_id }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_platform_order_id')"
              prop="platform_order_id"
              min-width="180"
            >
              <template slot-scope="{row}">{{ row.platform_order_id || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_created_at')"
              prop="created_at"
              width="160"
            >
              <template slot-scope="{row}">{{ formatDate(row.created_at) }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.platform')"
              prop="platform"
              width="120"
            >
              <template slot-scope="{row}">{{ row.platform || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.sale_channel')"
              prop="sale_channel_name"
              min-width="160"
            >
              <template slot-scope="{row}">{{ row.sale_channel_name || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_recipient')"
              prop="recipient_name"
              min-width="140"
            >
              <template slot-scope="{row}">{{ row.recipient_name || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_status')"
              prop="order_status_text"
              width="180"
            >
              <template slot-scope="{row}">{{ row.order_status_text || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_product_qty')"
              prop="pickup_quantity"
              align="right"
              width="140"
            >
              <template slot-scope="{row}">{{ row.pickup_quantity }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_tracking')"
              prop="tracking_number"
              min-width="160"
            >
              <template slot-scope="{row}">{{ row.tracking_number || '-' }}</template>
            </el-table-column>

            <el-table-column
              :label="$t('wms.packing_orders_view_order')"
              align="center"
              width="100"
            >
              <template slot-scope="{row}">
                <router-link
                  :to="'/backoffice/orders/' + row.order_id"
                  target="_blank"
                  class="btn btn-sm btn-info"
                >
                  <i class="fa fa-eye"></i>
                </router-link>
              </template>
            </el-table-column>
          </el-table>

          <div v-if="!loading && orders.length === 0" class="text-center py-4 text-muted">
            {{ $t('wms.packing_orders_empty') }}
          </div>
        </div>

        <div
          slot="footer"
          class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          v-if="summary.total_orders > 0"
        >
          <div>
            <p class="card-category">
              {{ $t('data') }} {{ (currentPage - 1) * pageSize + 1 }} {{ $t('to') }}
              {{ Math.min(currentPage * pageSize, summary.total_orders) }}
              {{ $t('of') }} {{ summary.total_orders }}
            </p>
          </div>
          <el-pagination
            background
            layout="prev, pager, next"
            :current-page="currentPage"
            :page-size="pageSize"
            :total="summary.total_orders"
            @current-change="handlePageChange"
          ></el-pagination>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Table, TableColumn, Pagination } from 'element-ui';
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import { mapActions } from 'vuex';

export default {
  name: 'AvailableProductPackingOrders',
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination,
  },
  data() {
    return {
      loading: false,
      orders: [],
      summary: {
        product_name: '',
        label: '',
        type: '',
        total_orders: 0,
        total_quantity: 0,
      },
      currentPage: 1,
      pageSize: 50,
    };
  },
  mounted() {
    var page = parseInt(this.$route.query.page) || 1;
    this.currentPage = page;
    this.loadData();
  },
  methods: {
    ...mapActions({
      packingOrdersList: 'Product/packingOrdersList',
    }),
    async loadData() {
      var query = this.$route.query;
      var productId = parseInt(query.product_id);
      if (!productId) {
        return;
      }

      var params = {
        type: query.type || 'platform',
        page: this.currentPage,
        page_size: this.pageSize,
      };

      if (query.platform_id) {
        params.platform_id = parseInt(query.platform_id);
      }
      if (query.sale_channel_id) {
        params.sale_channel_id = parseInt(query.sale_channel_id);
      }

      this.loading = true;
      try {
        var resp = await this.packingOrdersList({ productID: productId, params: params });
        var data = (resp.data && resp.data.Datum) || resp.data || {};
        this.summary = {
          product_name: data.product_name || '',
          label: (data.filter && data.filter.label) || '',
          type: (data.filter && data.filter.type) || query.type || '',
          total_orders: data.total_orders || 0,
          total_quantity: data.total_quantity || 0,
        };
        this.orders = data.orders || [];
      } catch (e) {
        console.log(e);
        this.orders = [];
      } finally {
        this.loading = false;
      }
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loadData();
    },
    formatDate(dateStr) {
      if (!dateStr) return '-';
      var d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      var yyyy = d.getFullYear();
      var mm = String(d.getMonth() + 1).padStart(2, '0');
      var dd = String(d.getDate()).padStart(2, '0');
      var hh = String(d.getHours()).padStart(2, '0');
      var min = String(d.getMinutes()).padStart(2, '0');
      return yyyy + '-' + mm + '-' + dd + ' ' + hh + ':' + min;
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
</style>
